import type { CustomSurveyQuestion, SubmitCustomSurveyAnswer } from '../../api/customSurveys';
import { QuestionType } from '../../api/customSurveyTemplates';

export type Answers = Record<number, SubmitCustomSurveyAnswer>;

export type QuestionGroup = {
  /** null når spørsmålene ikke har kategori */
  category: string | null;
  questions: CustomSurveyQuestion[];
};

/** Hva som mangler, så feilmeldingen kan si hva personen må gjøre */
export type MissingKind = 'pick' | 'many' | 'text';

const isText = (type: QuestionType) => type === QuestionType.SHORT_TEXT || type === QuestionType.LONG_TEXT;

export const sortQuestions = (questions: CustomSurveyQuestion[]) =>
  [...questions].sort((a, b) => a.orderIndex - b.orderIndex);

/** Kategoriene i spørsmålenes rekkefølge, spørsmål uten kategori til slutt */
export const groupQuestions = (questions: CustomSurveyQuestion[]): QuestionGroup[] => {
  const groups = new Map<string | null, CustomSurveyQuestion[]>();
  sortQuestions(questions).forEach((question) => {
    const category = question.category || null;
    groups.set(category, [...(groups.get(category) ?? []), question]);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => Number(a === null) - Number(b === null))
    .map(([category, grouped]) => ({ category, questions: grouped }));
};

export const isAnswered = (question: CustomSurveyQuestion, answer?: SubmitCustomSurveyAnswer) => {
  if (!answer) return false;
  if (question.questionType === QuestionType.MULTIPLE_SELECT || question.questionType === QuestionType.MULTIPLE_CHOICE) {
    return (answer.selectedOptions?.length ?? 0) > 0;
  }
  if (isText(question.questionType)) return Boolean(answer.textValue?.trim());
  return answer.numericValue !== undefined && answer.numericValue !== null;
};

export const missingKind = (question: CustomSurveyQuestion): MissingKind =>
  question.questionType === QuestionType.MULTIPLE_SELECT ? 'many' : isText(question.questionType) ? 'text' : 'pick';

/** Påkrevde spørsmål uten svar, i rekkefølge */
export const missingAnswers = (questions: CustomSurveyQuestion[], answers: Answers) =>
  sortQuestions(questions).filter((question) => question.isRequired && !isAnswered(question, answers[question.id]));

export const answeredCount = (questions: CustomSurveyQuestion[], answers: Answers) =>
  questions.filter((question) => isAnswered(question, answers[question.id])).length;

/** Anslått tid i hele minutter: rundt 15 sekunder per valg og 45 sekunder per tekstsvar */
export const estimatedMinutes = (questions: CustomSurveyQuestion[]) => {
  const seconds = questions.reduce((sum, question) => sum + (isText(question.questionType) ? 45 : 15), 0);
  return Math.max(1, Math.ceil(seconds / 60));
};

/** Svarene som sendes inn, uten tomme tekstsvar og uten tomme valg */
export const answersToSubmit = (questions: CustomSurveyQuestion[], answers: Answers): SubmitCustomSurveyAnswer[] =>
  questions
    .filter((question) => isAnswered(question, answers[question.id]))
    .map((question) => {
      const answer = answers[question.id];
      return isText(question.questionType) ? { ...answer, textValue: answer.textValue?.trim() } : answer;
    });
